import React, { useEffect, useState } from "react";
import { Button, Divider, Grid, TextField, Typography } from "@mui/material";
import { connect } from "react-redux";
import { FormValidation } from "../../../core/utility";
import { LocalService } from "../../../core/services";
import { UserService } from "../../../core/services";
import * as gtUtil from "../../../core/utility";

const localService = new LocalService();

const userService = new UserService();
interface Props {
    UserData: any;
}

const PasswordSecurity: React.FC<Props> = (props: Props) => {
    const formValidation = new FormValidation();
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [isEditingPassword, setIsEditingPassword] = useState(true);
    const [timerState, setTimerState] = useState({
        isDisabledPassword: false,
        passwordTimeLeft: 0
    });

    const [errors, setErrors] = useState<{
        currentPassword?: string;
        newPassword?: string;
        confirmPassword?: string;
    }>({});

    const handleCurrentPasswordChange = (val: string) => {
        setCurrentPassword(val);
        if (errors.currentPassword) {
            setErrors((prev) => ({ ...prev, currentPassword: "" }));
        }
    };

    const handleNewPasswordChange = (val: string) => {
        setNewPassword(val);
        if (errors.newPassword) {
            setErrors((prev) => ({ ...prev, newPassword: "" }));
        }
    };

    const handleConfirmPasswordChange = (val: string) => {
        setConfirmPassword(val);
        if (errors.confirmPassword) {
            setErrors((prev) => ({ ...prev, confirmPassword: "" }));
        }
    };

    const handleSave = async () => {
        const isEmpty = (val: string) => !val.trim();
        const newErrors: { [key: string]: string } = {};

        if (isEditingPassword) {
            // Empty field checks
            if (isEmpty(currentPassword)) {
                newErrors.currentPassword = "Current password is required";
            }
            if (isEmpty(newPassword)) {
                newErrors.newPassword = "New password is required";
            }
            if (isEmpty(confirmPassword)) {
                newErrors.confirmPassword = "Please confirm your new password";
            }

            // Proceed if all fields are filled
            if (!isEmpty(currentPassword) && !isEmpty(newPassword) && !isEmpty(confirmPassword)) {
                if (newPassword !== confirmPassword) {
                    newErrors.confirmPassword = "Passwords do not match";
                }

                const passwordValidation = formValidation.GetPasswordValidate(newPassword);
                if (!passwordValidation.status) {
                    newErrors.newPassword = passwordValidation.message;
                }
            }

            if (Object.keys(newErrors).length > 0) {
                setErrors(newErrors);
                return;
            }

            setErrors({}); // Clear errors if all validations pass

            try {
                const response = await userService.updatePassword({
                    data: {
                        old_password: currentPassword,
                        new_password: newPassword,
                        confirm_password: confirmPassword,
                        user_id: props.UserData.user_id,
                    },
                });

                if (response.status === gtUtil.Constants.SUCCESS) {
                    localService.toastify("Password Updated", "success", 1000);
                    setCurrentPassword("");
                    setNewPassword("");
                    setConfirmPassword("");
                } else {
                    localService.toastify(response.info, "error", 1000);
                    if (response.info === 'Current password is incorrect') {
                        setErrors({ currentPassword: response.info });
                    }
                }
            } catch (error) {
                localService.toastify("Password update failed", "error", 1000);
            }
        }
    };

    const forgetPasswordPin = (login: string, forgot: string) => {
        if (!login.trim()) {
            localService.toastify("Valid Email or Mobile Number is required", "error", 1000);
            return;
        }

        userService.forgetPasswordPin({ login, forgot }).then((res) => {
            if (res.status === gtUtil.Constants.SUCCESS) {
                localService.toastify(res.info, "success", 1000);

                const countdownTime = 180; // 3 minutes

                setTimerState((prevState) => ({
                    ...prevState,
                    isDisabledPassword: forgot === "forgot password" ? true : prevState.isDisabledPassword,
                    passwordTimeLeft: forgot === "forgot password" ? countdownTime : prevState.passwordTimeLeft
                }));

                setTimeout(() => {
                    setTimerState((prevState) => ({
                        ...prevState,
                        isDisabledPassword: forgot === "forgot password" ? false : prevState.isDisabledPassword
                    }));
                }, countdownTime * 1000);
            } else {
                setTimerState({
                    isDisabledPassword: false,
                    passwordTimeLeft: 0
                });
                localService.toastify(res.info, "error", 1000);
            }
        });
    };

    useEffect(() => {
        if (timerState.passwordTimeLeft <= 0) return;

        const timer = setInterval(() => {
            setTimerState((prevState) => ({
                ...prevState,
                passwordTimeLeft: prevState.passwordTimeLeft > 0 ? prevState.passwordTimeLeft - 1 : 0,
            }));
        }, 1000);

        return () => clearInterval(timer);
    }, [timerState.passwordTimeLeft]);

    return (
        <Grid container direction={{ xs: "column", sm: "row" }}>
            <Grid item xs={12}>
                <span className="fs-16 d-block" style={{ marginBottom: 10 }}>Password</span>
                <Grid container spacing={2}>
                    <Grid item xs={12} className="d-flex flex-column align-items-end">
                        <TextField
                            label="Current Password"
                            type="password"
                            value={currentPassword}
                            onChange={(e) => handleCurrentPasswordChange(e.target.value)}
                            fullWidth
                            size="small"
                            className="input-common"
                            error={!!errors.currentPassword}
                            helperText={errors.currentPassword}
                        />
                        <div className="d-flex pb-2">
                            <span
                                style={{
                                    fontSize: '12px',
                                    color: timerState.isDisabledPassword ? '#aaa' : '#000',
                                    cursor: timerState.isDisabledPassword ? 'not-allowed' : 'pointer',
                                    pointerEvents: timerState.isDisabledPassword ? 'none' : 'auto'
                                }}
                                onClick={() => {
                                    if (!timerState.isDisabledPassword) {
                                        forgetPasswordPin(props.UserData.login_email, "forgot password");
                                    }
                                }}
                            >
                                Forgot Password?
                            </span>

                            {timerState.isDisabledPassword && timerState.passwordTimeLeft > 0 && (
                                <span className="error" style={{ marginLeft: '8px', fontSize: '0.8rem' }}>
                                    {Math.floor(timerState.passwordTimeLeft / 60)}:
                                    {(timerState.passwordTimeLeft % 60).toString().padStart(2, "0")}
                                </span>
                            )}
                        </div>
                    </Grid>
                    <Grid item xs={12} className="pt-0">
                        <TextField
                            label="New Password"
                            type="password"
                            value={newPassword}
                            onChange={(e) => handleNewPasswordChange(e.target.value)}
                            fullWidth
                            size="small"
                            className="input-common"
                            error={!!errors.newPassword}
                            helperText={errors.newPassword}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Confirm Password"
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => handleConfirmPasswordChange(e.target.value)}
                            fullWidth
                            size="small"
                            className="input-common"
                            error={!!errors.confirmPassword}
                            helperText={errors.confirmPassword}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Button
                            variant="contained"
                            className="btn btn-outline-primary upload_button"
                            onClick={handleSave}
                        >
                            Update Password
                        </Button>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
};

const mapStateToProps = (state: any) => {
    return {
        UserData: state.UserReducer.UserData,
    };
};
const connectToRedux = connect(mapStateToProps, null)(PasswordSecurity);
export { connectToRedux as PasswordSecurity };